/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo } from 'react';
import Chart from 'chart.js/auto';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faChartPie,
    faFilePdf,
    faFileZipper,
    faUsers,
    faCommentDots,
    faClipboardQuestion,
    faDownload,
    faChevronDown,
    faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const API = "https://api18081113.queroapp.pl";

// --- HELPERS FROM EXCEL EXPORTER --- (Migrated)
// --- HELPERS FROM EXCEL EXPORTER --- (Migrated)
const stripIds = (name = "", secretId = "") => {
    let s = name || "";
    const sid = (secretId || "").trim();
    if (!s) return s;

    let prev;
    do {
        prev = s;
        if (sid) s = s.replace(new RegExp(`_${sid}$`), "");
        s = s.replace(/^[mM]essages_/, ""); // Remove leading messages_ prefix
        s = s.replace(/_[a-z0-9]{6,16}$/i, ""); // Remove trailing stage/segment ID
    } while (s !== prev);

    return s;
};

const prettyLabel = (name = "", secretId = "") =>
    stripIds(name, secretId).replace(/_/g, " ").trim();

const extractPrelegents = (json) => {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.prelegents)) return json.prelegents;
    if (Array.isArray(json?.data)) return json.data;
    if (Array.isArray(json?.segments)) return json.segments;
    return [];
};

const getPrelegentSegmentName = (p) =>
    p?.segment_name || p?.stage_name || p?.segment || p?.name || "";

const getPrelegentDisplay = (p) => ({
    name: p?.stage_name || p?.fullname || p?.full_name || p?.name || "Prelegent",
});

const sanitizeFilename = (name) => String(name).replace(/[^a-z0-9ąćęłńóśźż ]/gi, "_").trim();

const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });

const getLogo = async () => {
    try {
        const logoBlob = await fetch("/sayq-fav.svg", { cache: "no-store" }).then((r) => r.blob());
        return await blobToBase64(logoBlob);
    } catch (_) {
        return null;
    }
};

const normalizeRows = (rows) =>
    (Array.isArray(rows) ? rows : [])
        .filter((r) => typeof r?.message === "string" && r.message.trim().length > 0)
        .map((r) => ({ ...r, message: r.message.trim() }));
// ----------------------------------------------------

export default function StatisticsView({ secretId, closeView, eventName }) {
    const [loading, setLoading] = useState(true);
    const [statsData, setStatsData] = useState(null);
    const [surveyData, setSurveyData] = useState([]);
    const [activeTab, setActiveTab] = useState('overview'); // overview | surveys | reports
    const [peakActive, setPeakActive] = useState(0);

    // --- FETCH DATA ---
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                // 1. Message Data (Big Query)
                const msgsRes = await fetch(`${API}/event/get-all-stage-data`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ secredId: secretId }),
                });
                const msgsData = await msgsRes.json();

                // 2. Surveys Data
                const survRes = await fetch(`${API}/get-surveys/${encodeURIComponent(secretId)}`);
                const survDataJson = await survRes.json();

                // 3. Local Peak Active
                const storedPeak = localStorage.getItem(`peak_active_${secretId}`);
                if (storedPeak) setPeakActive(parseInt(storedPeak, 10));

                // 4. Fetch Prelegent Info for real names
                let prelegentMap = {};
                try {
                    const infoRes = await fetch(`${API}/get-prelegent-info/${secretId}`);
                    const infoJson = await infoRes.json();
                    const list = extractPrelegents(infoJson);
                    list.forEach((p) => {
                        const base = stripIds(getPrelegentSegmentName(p), secretId);
                        const { name } = getPrelegentDisplay(p);
                        if (base && !prelegentMap[base]) prelegentMap[base] = name;
                    });
                } catch (e) { console.error("Could not fetch prelegent info", e); }

                if (msgsData?.tables) {
                    processMessageStats(msgsData.tables, prelegentMap);
                }
                if (survDataJson?.surveys) {
                    setSurveyData(survDataJson.surveys);
                }

            } catch (e) {
                console.error("Stats load error", e);
            } finally {
                setLoading(false);
            }
        };

        if (secretId) load();
    }, [secretId]);

    const processMessageStats = (tables, prelegentMap) => {
        const entries = Object.entries(tables);
        const segments = entries
            .map(([tableName, rows]) => {
                const validRows = normalizeRows(rows);

                const baseClean = stripIds(tableName, secretId);
                let displayName = prelegentMap[baseClean] || prettyLabel(baseClean, secretId) || "Segment";
                if (displayName.length > 50) displayName = displayName.slice(0, 50);

                return {
                    rawName: tableName,
                    label: displayName,
                    rows: validRows,
                    count: validRows.length
                };
            })
            .filter((seg) => seg.count > 0);

        // Sort by activity
        segments.sort((a, b) => b.count - a.count);

        const totalMessages = segments.reduce((sum, s) => sum + s.count, 0);
        // Estimate unique users (naive)
        const allUsernames = new Set();
        segments.forEach(seg => {
            seg.rows.forEach(r => {
                if (r.username) allUsernames.add(r.username.trim());
            });
        });

        setStatsData({
            segments,
            totalMessages,
            uniqueParticipants: allUsernames.size
        });
    };

    // --- CHARTS COMPONENTS ---
    const OverviewChart = ({ segments }) => {
        const canvasRef = React.useRef(null);
        const chartRef = React.useRef(null);

        useEffect(() => {
            if (!canvasRef.current || !segments) return;

            const ctx = canvasRef.current.getContext('2d');
            if (chartRef.current) chartRef.current.destroy();

            chartRef.current = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: segments.slice(0, 8).map(s => s.label).concat(segments.length > 8 ? ['Inne'] : []),
                    datasets: [{
                        data: segments.slice(0, 8).map(s => s.count).concat(segments.length > 8 ? [segments.slice(8).reduce((a, b) => a + b.count, 0)] : []),
                        backgroundColor: [
                            '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
                            '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
                        ],
                        borderWidth: 0,
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { position: 'right' }
                    },
                    cutout: '60%'
                }
            });

            return () => { if (chartRef.current) chartRef.current.destroy(); }
        }, [segments]);

        return <div style={{ height: 300, position: 'relative' }}><canvas ref={canvasRef} /></div>;
    };

    const SurveyChart = ({ survey }) => {
        const canvasRef = React.useRef(null);
        const chartRef = React.useRef(null);

        useEffect(() => {
            if (!canvasRef.current) return;
            const ctx = canvasRef.current.getContext('2d');
            if (chartRef.current) chartRef.current.destroy();

            const labels = survey.options.map(o => o.option_text.substring(0, 20) + (o.option_text.length > 20 ? '...' : ''));
            const data = survey.options.map(o => o.votes);

            chartRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [{
                        label: 'Głosy',
                        data,
                        backgroundColor: '#0d6efd',
                        borderRadius: 4,
                    }]
                },
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } }
                }
            });

            return () => { if (chartRef.current) chartRef.current.destroy(); }
        }, [survey]);

        return <div style={{ height: 150 + (survey.options.length * 20), position: 'relative' }}><canvas ref={canvasRef} /></div>;
    };


    // --- EXPORT logic (from ExcelExporter) ---
    // [Previously handled inside ExcelExporter, now integrated]
    // Re-implementing makePieChartDataUrl for PDF specifically
    const makePieChartDataUrl = async (labels, counts) => {
        // Create hidden canvas
        const size = 600;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");

        const chart = new Chart(ctx, {
            type: "doughnut",
            data: {
                labels,
                datasets: [{
                    data: counts,
                    backgroundColor: ['#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14', '#ffc107', '#198754'],
                }]
            },
            options: {
                animation: false,
                responsive: false,
                plugins: {
                    legend: { display: false } // Default Chart.js legend disabled for better PDF readability
                }
            }
        });

        // Wait for render (Chart.js is synchronous for image gen typically if animation off, but safe to wait tick)
        await new Promise(r => setTimeout(r, 10)); // tiny delay
        const url = canvas.toDataURL("image/png");
        chart.destroy();
        return url;
    };

    // Helper for Bar Chart (Survey)
    const makeBarChartDataUrl = async (labels, data, label) => {
        const width = 600;
        const height = 300;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        // Clear white background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);

        const chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels,
                datasets: [{
                    label: label || 'Głosy',
                    data: data,
                    backgroundColor: '#0d6efd',
                    barThickness: 40,
                }]
            },
            options: {
                animation: false,
                responsive: false,
                indexAxis: 'y', // Horizontal bars look better in reports
                plugins: { legend: { display: false } },
                scales: {
                    x: { beginAtZero: true, ticks: { font: { size: 14 } } },
                    y: { ticks: { font: { size: 14, weight: 'bold' } } }
                }
            }
        });

        await new Promise(r => setTimeout(r, 50));
        const url = canvas.toDataURL("image/png");
        chart.destroy();
        return url;
    };


    const handleExportFull = async () => {
        if (!statsData || !statsData.segments.length) return alert("Brak danych do raportu.");
        const pdfMake = window?.pdfMake;
        if (!pdfMake) return alert("Błąd biblioteki PDF");

        setLoading(true);
        try {
            const logoDataUrl = await getLogo();
            const segments = statsData.segments;

            // 1. Generate PIE CHART
            const labels = segments.slice(0, 8).map(s => s.label);
            const counts = segments.slice(0, 8).map(s => s.count);
            const pieUrl = await makePieChartDataUrl(labels, counts);

            // 2. Generate SURVEY CHARTS
            const surveyImages = [];
            for (const s of surveyData) {
                const sLabels = s.options.map(o => o.option_text.substring(0, 25));
                const sVotes = s.options.map(o => o.votes);
                const url = await makeBarChartDataUrl(sLabels, sVotes, "Głosy");
                surveyImages.push({ id: s.id, url, question: s.question, total: s.total_votes });
            }

            const content = [];

            // --- HEADER ---
            // Colorful top strip simulation
            content.push({ canvas: [{ type: 'rect', x: 0, y: -60, w: 595, h: 14, color: '#0d6efd' }], absolutePosition: { x: 0, y: 0 } });

            // Modern Header
            content.push({
                columns: [
                    {
                        width: 100,
                        stack: [
                            logoDataUrl ? { image: logoDataUrl, width: 60, alignment: 'center' } : { text: '', width: 60 },
                            { text: "SayQ App", fontSize: 8, color: '#888', alignment: 'center', margin: [0, 5, 0, 0] }
                        ]
                    },
                    {
                        stack: [
                            { text: eventName || "Raport Wydarzenia", style: "headerTitle" },
                            { text: "Raport Statystyk", style: "headerSub", color: '#888' }
                        ],
                        margin: [10, 10, 0, 0]
                    },
                    {
                        stack: [
                            { text: new Date().toLocaleDateString(), alignment: 'right', bold: true },
                            { text: `ID: ${secretId}`, alignment: 'right', color: '#888', fontSize: 10 }
                        ]
                    }
                ],
                margin: [0, 0, 0, 30]
            });

            content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#eee' }], margin: [0, 0, 0, 30] });


            // --- SUMMARY SECTION ---
            content.push({ text: "PODSUMOWANIE", style: "sectionTitle" });

            // Make Custom Legend Table
            const colors = ['#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545', '#fd7e14', '#ffc107', '#198754'];
            const legendBody = segments.slice(0, 8).map((s, i) => [
                { canvas: [{ type: 'rect', x: 0, y: 3, w: 8, h: 8, color: colors[i] }] },
                { text: s.label, fontSize: 9, color: '#333', margin: [5, 0, 0, 0] },
                { text: String(s.count), fontSize: 9, bold: true, alignment: 'right' }
            ]);

            content.push({
                columns: [
                    {
                        width: '55%',
                        stack: [
                            { text: "Statystyki ogólne", style: "subHeader" },
                            {
                                table: {
                                    widths: ['*', 'auto'],
                                    body: [
                                        [{ text: "Wiadomości", color: '#555' }, { text: String(statsData.totalMessages), bold: true, alignment: 'right' }],
                                        [{ text: "Uczestnicy", color: '#555' }, { text: String(statsData.uniqueParticipants), bold: true, alignment: 'right' }],
                                        [{ text: "Rekord Online", color: '#555' }, { text: String(peakActive), bold: true, alignment: 'right' }],
                                        [{ text: "Ankiety", color: '#555' }, { text: String(surveyData.length), bold: true, alignment: 'right' }],
                                        [{ text: "Segmenty", color: '#555' }, { text: String(segments.length), bold: true, alignment: 'right' }],
                                    ]
                                },
                                layout: 'noBorders', // Clean look
                                margin: [0, 10, 20, 20]
                            },
                            // CUSTOM LEGEND
                            { text: "Aktywność w segmentach (Top 8)", style: "subHeader", fontSize: 11, margin: [0, 0, 0, 5] },
                            {
                                table: {
                                    widths: [15, '*', 'auto'],
                                    body: legendBody
                                },
                                layout: 'noBorders',
                                margin: [0, 0, 20, 0]
                            }
                        ]
                    },
                    {
                        width: '45%',
                        stack: [
                            pieUrl ? { image: pieUrl, width: 160 } : { text: "Brak wykresu" }
                        ],
                        alignment: 'center',
                        margin: [0, 10, 0, 0]
                    }
                ],
                margin: [0, 0, 0, 40]
            });

            // --- SURVEYS SECTION ---
            if (surveyImages.length > 0) {
                content.push({ text: "WYNIKI ANKIET", style: "sectionTitle", pageBreak: 'before' });

                surveyImages.forEach(s => {
                    content.push({
                        stack: [
                            { text: s.question, style: "subHeader", margin: [0, 20, 0, 5] },
                            { text: `Łącznie głosów: ${s.total}`, color: '#666', fontSize: 10, margin: [0, 0, 0, 10] },
                            { image: s.url, width: 450, alignment: 'center' }
                        ],
                        unbreakable: true
                    });
                    content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#eee', dash: { length: 2 } }], margin: [0, 20] });
                });
            }


            // --- SEGMENTS SECTION ---
            content.push({ text: "SEGMENTY PYTAŃ", style: "sectionTitle", pageBreak: 'before' });

            segments.forEach((seg, idx) => {
                content.push({ text: seg.label, style: "segmentTitle", margin: [0, 20, 0, 10] });

                const body = [
                    [{ text: "AUTOR", style: "tableHeader" }, { text: "TREŚĆ WIADOMOŚCI", style: "tableHeader" }],
                    ...seg.rows.map((r, i) => [
                        { text: r.username || "Anonim", fillColor: i % 2 === 0 ? '#f8f9fa' : '#fff', color: '#444' },
                        { text: r.message || "", fillColor: i % 2 === 0 ? '#f8f9fa' : '#fff', color: '#222' }
                    ])
                ];

                content.push({
                    table: {
                        headerRows: 1,
                        widths: ['25%', '75%'],
                        body
                    },
                    layout: {
                        hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 1,
                        vLineWidth: (i, node) => 0,
                        hLineColor: (i, node) => (i === 0 || i === 1) ? '#0d6efd' : '#eeeeee',
                    }
                });
            });

            const docDefinition = {
                pageSize: "A4",
                pageMargins: [40, 60, 40, 60],
                content,
                styles: {
                    headerTitle: { fontSize: 20, bold: true, color: '#222' },
                    headerSub: { fontSize: 12, italics: true },
                    sectionTitle: { fontSize: 16, bold: true, color: '#0d6efd', margin: [0, 0, 0, 10], characterSpacing: 1 },
                    subHeader: { fontSize: 13, bold: true, color: '#333' },
                    segmentTitle: { fontSize: 14, bold: true, color: '#444' },
                    tableHeader: { bold: true, fontSize: 10, color: '#0d6efd', fillColor: '#fff' } // Minimalist header
                },
                defaultStyle: {
                    font: 'Roboto', // standard pdfmake font
                    fontSize: 10
                }
            };

            pdfMake.createPdf(docDefinition).download(`Raport_SayQ_${secretId}.pdf`);

        } catch (e) {
            console.error("PDF Generate Error:", e);
            alert("Błąd generowania PDF.");
        } finally {
            setLoading(false);
        }
    };

    const handleExportPartials = async () => {
        if (!statsData || !statsData.segments.length) return alert("Brak danych.");
        const pdfMake = window?.pdfMake;
        if (!pdfMake) return alert("Błąd biblioteki PDF");

        setLoading(true);
        try {
            const logoDataUrl = await getLogo();
            const zip = new JSZip();

            for (const seg of statsData.segments) {
                const docDef = {
                    pageSize: "A4",
                    pageMargins: [40, 60, 40, 60],
                    content: [],
                    styles: {
                        headerTitle: { fontSize: 20, bold: true, color: '#222' },
                        headerSub: { fontSize: 12, italics: true },
                        segmentTitle: { fontSize: 16, bold: true, color: '#0d6efd', margin: [0, 20, 0, 10] },
                        tableHeader: { bold: true, fontSize: 10, color: '#0d6efd', fillColor: '#fff' }
                    },
                    defaultStyle: { font: 'Roboto', fontSize: 10 }
                };

                // --- HEADER ---
                docDef.content.push({ canvas: [{ type: 'rect', x: 0, y: -60, w: 595, h: 14, color: '#0d6efd' }], absolutePosition: { x: 0, y: 0 } });

                docDef.content.push({
                    columns: [
                        {
                            width: 100,
                            stack: [
                                logoDataUrl ? { image: logoDataUrl, width: 60, alignment: 'center' } : { text: '', width: 60 },
                                { text: "SayQ App", fontSize: 8, color: '#888', alignment: 'center', margin: [0, 5, 0, 0] }
                            ]
                        },
                        {
                            stack: [
                                { text: eventName || "Raport Segmentu", style: "headerTitle" },
                                { text: `Segment: ${seg.label}`, style: "headerSub", color: '#555' }
                            ],
                            margin: [10, 10, 0, 0]
                        },
                        {
                            stack: [
                                { text: new Date().toLocaleDateString(), alignment: 'right', bold: true },
                                { text: `ID: ${secretId}`, alignment: 'right', color: '#888', fontSize: 10 }
                            ]
                        }
                    ],
                    margin: [0, 0, 0, 30]
                });

                docDef.content.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#eee' }], margin: [0, 0, 0, 30] });

                // --- TABLE ---
                docDef.content.push({ text: seg.label, style: "segmentTitle" });

                const body = [
                    [{ text: "AUTOR", style: "tableHeader" }, { text: "TREŚĆ WIADOMOŚCI", style: "tableHeader" }],
                    ...seg.rows.map((r, i) => [
                        { text: r.username || "Anonim", fillColor: i % 2 === 0 ? '#f8f9fa' : '#fff', color: '#444' },
                        { text: r.message || "", fillColor: i % 2 === 0 ? '#f8f9fa' : '#fff', color: '#222' }
                    ])
                ];

                docDef.content.push({
                    table: {
                        headerRows: 1,
                        widths: ['25%', '75%'],
                        body
                    },
                    layout: {
                        hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 1,
                        vLineWidth: (i, node) => 0,
                        hLineColor: (i, node) => (i === 0 || i === 1) ? '#0d6efd' : '#eeeeee',
                    }
                });

                const pdfBlob = await new Promise(resolve => {
                    pdfMake.createPdf(docDef).getBlob(resolve);
                });
                zip.file(`${sanitizeFilename(seg.label)}.pdf`, pdfBlob);
            }

            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `Raporty_Czastkowe_${secretId}.zip`);

        } catch (e) {
            console.error(e);
            alert("Błąd generowania ZIP.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-white d-flex flex-column" style={{ zIndex: 2000 }}>
            {/* Header */}
            <div className="w-100 px-4 py-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                <button
                    onClick={closeView}
                    className="btn btn-white rounded-circle shadow-sm border"
                    style={{ width: 44, height: 44 }}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h4 className="fw-bold m-0 text-dark">Statystyki i Raporty</h4>
                <div style={{ width: 44 }}></div>
            </div>

            {loading && !statsData ? (
                <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            ) : (
                <div className="flex-grow-1 overflow-y-auto bg-light">
                    <div className="container py-5" style={{ maxWidth: 1000 }}>

                        {/* 1. OVERVIEW CARDS */}
                        <div className="row g-3 mb-5">
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm p-4 rounded-4 h-100 bg-white">
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex center" style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
                                            <FontAwesomeIcon icon={faCommentDots} />
                                        </div>
                                        <h6 className="text-secondary fw-bold m-0 text-uppercase ls-1" style={{ fontSize: '0.75rem' }}>Wiadomości</h6>
                                    </div>
                                    <h2 className="fw-bold m-0">{statsData?.totalMessages || 0}</h2>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm p-4 rounded-4 h-100 bg-white">
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="bg-success bg-opacity-10 text-success rounded-circle d-flex center" style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
                                            <FontAwesomeIcon icon={faUsers} />
                                        </div>
                                        <h6 className="text-secondary fw-bold m-0 text-uppercase ls-1" style={{ fontSize: '0.75rem' }}>Uczestnicy</h6>
                                    </div>
                                    <h2 className="fw-bold m-0">{statsData?.uniqueParticipants || 0}</h2>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm p-4 rounded-4 h-100 bg-white">
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-flex center" style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
                                            <FontAwesomeIcon icon={faChartPie} />
                                        </div>
                                        <h6 className="text-secondary fw-bold m-0 text-uppercase ls-1" style={{ fontSize: '0.75rem' }}>Rekord Online</h6>
                                    </div>
                                    <h2 className="fw-bold m-0">{peakActive}</h2>
                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>max w jednej chwili (sesja)</small>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm p-4 rounded-4 h-100 bg-white">
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                        <div className="bg-warning bg-opacity-10 text-warning rounded-circle d-flex center" style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}>
                                            <FontAwesomeIcon icon={faClipboardQuestion} />
                                        </div>
                                        <h6 className="text-secondary fw-bold m-0 text-uppercase ls-1" style={{ fontSize: '0.75rem' }}>Ankiety</h6>
                                    </div>
                                    <h2 className="fw-bold m-0">{surveyData?.length || 0}</h2>
                                </div>
                            </div>
                        </div>

                        {/* 2. CHARTS SECTION */}
                        <div className="row g-4 mb-5">
                            <div className="col-lg-6">
                                <div className="card border-0 shadow-sm rounded-4 h-100 p-4">
                                    <h5 className="fw-bold mb-4">Aktywność w segmentach</h5>
                                    {statsData?.segments?.length > 0 ? (
                                        <OverviewChart segments={statsData.segments} />
                                    ) : (
                                        <div className="text-center text-muted py-5">Brak danych</div>
                                    )}
                                </div>
                            </div>

                            <div className="col-lg-6">
                                <div className="card border-0 shadow-sm rounded-4 h-100 p-4">
                                    <h5 className="fw-bold mb-4">Centrum Raportów</h5>
                                    <p className="text-secondary small mb-4">Pobierz szczegółowe zestawienia w formacie PDF.</p>

                                    <button
                                        onClick={handleExportFull}
                                        disabled={loading}
                                        className="btn btn-outline-primary w-100 py-3 rounded-3 mb-3 d-flex align-items-center justify-content-between hover-bg-primary hover-text-white transition-all group"
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <FontAwesomeIcon icon={faFilePdf} className="fs-4" />
                                            <div className="text-start">
                                                <div className="fw-bold">Pełny Raport</div>
                                                <div className="small opacity-75">Wszystkie segmenty w jednym pliku</div>
                                            </div>
                                        </div>
                                        <FontAwesomeIcon icon={faDownload} />
                                    </button>

                                    <button
                                        onClick={handleExportPartials}
                                        disabled={loading}
                                        className="btn btn-outline-dark w-100 py-3 rounded-3 d-flex align-items-center justify-content-between transition-all"
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <FontAwesomeIcon icon={faFileZipper} className="fs-4" />
                                            <div className="text-start">
                                                <div className="fw-bold">Raporty Cząstkowe</div>
                                                <div className="small opacity-75">Paczka ZIP (osobny plik na segment)</div>
                                            </div>
                                        </div>
                                        <FontAwesomeIcon icon={faDownload} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* 3. SURVEY DETAILS (Collapsible?) */}
                        <div className="card border-0 shadow-sm rounded-4 p-4">
                            <h5 className="fw-bold mb-4">Wyniki Ankiet</h5>
                            {surveyData.length === 0 ? (
                                <p className="text-muted">Brak ankiet w tym wydarzeniu.</p>
                            ) : (
                                <div className="d-flex flex-column gap-5">
                                    {surveyData.map(s => (
                                        <div key={s.id} className="row align-items-center">
                                            <div className="col-md-4">
                                                <h6 className="fw-bold mb-2">{s.question}</h6>
                                                <span className="badge bg-secondary rounded-pill">{s.total_votes} głosów</span>
                                                <div className="mt-3 small text-secondary">
                                                    Status: {Number(s.is_active) ? <span className="text-success fw-bold">Aktywna</span> : "Zakończona"}
                                                </div>
                                            </div>
                                            <div className="col-md-8">
                                                <SurveyChart survey={s} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}
